import { useEffect } from "react";
import { LoaderFunctionArgs, useLoaderData, Link } from "react-router";
import { Helmet } from "@dr.pogodin/react-helmet";
import {
  ICONS_PREPEND,
  IS_ARCHIVER_ENABLED,
  KEMONO_SITE,
  SITE_NAME,
} from "#env/env-vars";
import { fetchPost, fetchPostComments } from "#api/posts";
import { PageSkeleton } from "#components/pages";
import { SliderAd } from "#components/advs";
import { paysites } from "#entities/paysites";
import {
  IComment,
  IPost,
  IPostAttachment,
  IPostPreview,
  IPostVideo,
  PostOverview,
} from "#entities/posts";
import { Creator, fetchCreator } from "#api/creators";

import * as styles from "./post.module.scss";

interface IProps {
  post: IPost;
  creator: Creator;
  revisions: Awaited<ReturnType<typeof fetchPost>>["props"]["revisions"];
  flagged: string | null;
  videos?: IPostVideo[];
  attachments?: IPostAttachment[];
  previews?: IPostPreview[];
  archives_enabled?: boolean;
  comments: Promise<IComment[]>;
}

export function PostPage() {
  const {
    post,
    creator,
    revisions,
    flagged,
    videos,
    attachments,
    previews,
    archives_enabled,
    comments,
  } = useLoaderData() as IProps;
  const paysite = paysites[post.service];
  const postTitle = post.title ?? "Untitled";
  const artistName = creator.name ?? post.user;

  const title = !creator
    ? `Post "${postTitle}"`
    : `Post "${postTitle}" by "${artistName}" from ${paysite.title}`;

  function handlePrevNextLinks(event: KeyboardEvent) {
    switch (event.key) {
      case "ArrowLeft":
        document
          .querySelector<HTMLAnchorElement>(".post__nav-link.prev")
          ?.click();
        break;
      case "ArrowRight":
        document
          .querySelector<HTMLAnchorElement>(".post__nav-link.next")
          ?.click();
        break;
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handlePrevNextLinks);

    return () => {
      document.removeEventListener("keydown", handlePrevNextLinks);
    };
  }, []);

  return (
    <PageSkeleton name="post" title={title}>
      <Helmet>
        <meta name="service" content={post.service} />
        <meta name="id" content={post.id} />
        <meta name="user" content={post.user} />
        {!post.published ? undefined : (
          <meta name="published" content={post.published} />
        )}

        {/* <link rel="canonical" href="{{ g.canonical_url }}" /> */}

        <meta property="og:title" content={title} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta
          property="og:image"
          content={`${ICONS_PREPEND ?? KEMONO_SITE}/icons/${post.service}/${
            post.user
          }`}
        />
        {/* <meta property="og:url" content="{{ g.canonical_url }}" /> */}
      </Helmet>

      <SliderAd />

      <nav className="post__nav-links">
        <ul className="post__nav-list">
          {!post.prev ? (
            <li className="post__nav-item subtitle">‹ previous</li>
          ) : (
            <li className="post__nav-item">
              <Link
                className="post__nav-link prev"
                to={`/${post.service}/user/${post.user}/post/${post.prev}`}
              >
                ‹ previous
              </Link>
            </li>
          )}

          {!post.next ? (
            <li className="post__nav-item subtitle">next ›</li>
          ) : (
            <li className="post__nav-item">
              <Link
                className="post__nav-link next"
                to={`/${post.service}/user/${post.user}/post/${post.next}`}
              >
                next ›
              </Link>
            </li>
          )}
        </ul>
      </nav>

      <PostOverview
        post={post}
        creator={creator}
        revisions={revisions}
        flagged={flagged}
        videos={videos}
        attachments={attachments}
        previews={previews}
        archives_enabled={archives_enabled}
        comments={comments}
        postTitle={postTitle}
        paysite={paysite}
      />
    </PageSkeleton>
  );
}

export async function loader({ params }: LoaderFunctionArgs): Promise<IProps> {
  const service = params.service!.trim();
  const creatorId = params.creatorId!.trim();
  const postId = params.postId!.trim();

  const creator = fetchCreator(service, creatorId);
  const comments = fetchPostComments(service, creatorId, postId);

  const {
    post,
    attachments,
    previews,
    videos,
    props: { flagged, revisions },
  } = await fetchPost(service, creatorId, postId);

  return {
    post,
    creator: await creator,
    revisions,
    attachments,
    previews,
    videos,
    archives_enabled: IS_ARCHIVER_ENABLED,
    flagged,
    comments,
  };
}
